import { PUBLIC_OFFLINE_ROUTES } from './config';

export type RoleGroup =
  | 'public'
  | 'reception'
  | 'sales'
  | 'tele_sales'
  | 'manager'
  | 'hr'
  | 'accountant'
  | 'instructor'
  | 'employee'
  | 'student'
  | 'marketing'
  | 'developer'
  | 'general_manager';

export interface RoleOfflineManifest {
  role: string;
  groups: RoleGroup[];
  routes: string[];
}

const RECEPTION_ROUTES = [
  '/reception',
  '/reception/students?category=all',
  '/reception/groups',
  '/reception/quarter',
  '/reception/calendar',
  '/reception/finance',
  '/reception/branch-finance',
  '/reception/sales-invoices',
  '/reception/certificates',
  '/reception/mail',
  '/reception/pro-finance',
  '/reception/placement-payments',
  '/reception/refunds',
  '/reception/requests',
  '/reception/course-interests',
  '/reception/sales-consult-slots',
  '/reception/sales-consult-queue',
  '/reception/tasks',
  '/reception/tasks-manage',
  '/reception/daily-reports',
  '/reception/chat',
  '/reception/super/logs',
  '/reception/exams',
  '/reception/exams/new',
  '/reception/attendance/matrix',
  '/reception/language',
  '/reception/language/exams/import',
  '/reception/language/materials',
  '/reception/materials',
  '/reception/language/students/inactive',
] as const;

const SALES_ROUTES = [
  '/sales',
  '/sales/today',
  '/sales/performance',
  '/sales/pipeline',
  '/sales/tasks',
  '/sales/history',
  '/sales/mail',
  '/sales/lead_movement',
  '/sales/tele-sales',
  '/sales/facebook_mappings',
  '/sales/targets',
  '/sales/reports',
  '/sales/settings',
] as const;

const TELE_SALES_ROUTES = [
  '/sales',
  '/sales/tele-sales',
] as const;

const MANAGER_ROUTES = [
  '/manager',
  '/manager/employees',
  '/manager/tasks',
  '/manager/mail',
  '/manager/branch-transfers',
  '/manager/groups',
  '/manager/quarter',
  '/manager/students?category=all',
  '/manager/calendar',
  '/manager/super/logs',
  '/manager/finance',
  '/manager/pro-finance',
] as const;

const HR_ROUTES = [
  '/hr',
  '/hr/dashboard',
  '/hr/employees',
  '/hr/schedules',
  '/hr/messages',
  '/hr/mail',
  '/hr/requests',
  '/hr/payroll',
  '/hr/instructor-payroll',
  '/hr/group-rates',
  '/hr/attendance',
  '/hr/reports',
] as const;

const ACCOUNTANT_ROUTES = [
  '/accountant',
  '/accountant/dashboard',
  '/accountant/income',
  '/accountant/expenses',
  '/accountant/refunds',
  '/accountant/customer-estimates',
  '/accountant/reports',
  '/accountant/cash-flow',
  '/accountant/installments',
  '/accountant/group-rates',
] as const;

const INSTRUCTOR_ROUTES = [
  '/instructor',
  '/instructor/profile',
] as const;

const EMPLOYEE_ROUTES = [
  '/employee/profile',
  '/employee/messages',
  '/employee/mail',
  '/employee/requests',
  '/employee/attendance',
] as const;

const STUDENT_ROUTES = [
  '/student/dashboard',
  '/student/profile',
  '/languages/student/profile',
  '/languages/student/exams',
  '/student/live/viewer',
] as const;

const MARKETING_ROUTES = [
  '/marketing',
  '/marketing/courses',
  '/marketing/job-applications',
  '/marketing/contact-inquiries',
  '/marketing/mail',
  '/marketing/sales-invoices',
] as const;

const DEVELOPER_ROUTES = [
  '/developer/cockpit',
  '/developer/errors',
  '/developer/sandbox',
] as const;

const ROLE_GROUPS: Record<RoleGroup, readonly string[]> = {
  public: PUBLIC_OFFLINE_ROUTES,
  reception: RECEPTION_ROUTES,
  sales: SALES_ROUTES,
  tele_sales: TELE_SALES_ROUTES,
  manager: MANAGER_ROUTES,
  hr: HR_ROUTES,
  accountant: ACCOUNTANT_ROUTES,
  instructor: INSTRUCTOR_ROUTES,
  employee: EMPLOYEE_ROUTES,
  student: STUDENT_ROUTES,
  marketing: MARKETING_ROUTES,
  developer: DEVELOPER_ROUTES,
  general_manager: [
    ...RECEPTION_ROUTES,
    ...SALES_ROUTES,
    ...MANAGER_ROUTES,
    '/manager/import-leads',
    ...HR_ROUTES,
    ...ACCOUNTANT_ROUTES,
    ...MARKETING_ROUTES,
  ],
};

const ROLE_ALIAS: Record<string, RoleGroup> = {
  reception: 'reception',
  super_reception: 'reception',
  sales: 'sales',
  agent: 'sales',
  sales_manager: 'sales',
  tele_sales: 'tele_sales',
  ghosted_sales: 'sales',
  manager: 'manager',
  general_manager: 'general_manager',
  hr: 'hr',
  accountant: 'accountant',
  instructor: 'instructor',
  employee: 'employee',
  student: 'student',
  marketing: 'marketing',
  marketing_money: 'marketing',
  developer: 'developer',
};

export function normalizeRole(value: string | null | undefined): string {
  return String(value || '').trim().toLowerCase();
}

export function getRoleOfflineManifest(role: string | null | undefined): RoleOfflineManifest | null {
  const normalizedRole = normalizeRole(role);
  const mapped = ROLE_ALIAS[normalizedRole];
  if (!mapped) return null;
  const routes = [...new Set(ROLE_GROUPS[mapped])];
  return {
    role: normalizedRole,
    groups: mapped === 'general_manager' ? ['general_manager'] : [mapped],
    routes,
  };
}

export function getRoleOfflineRoutes(role: string | null | undefined): string[] {
  const manifest = getRoleOfflineManifest(role);
  return manifest ? manifest.routes : [];
}

export function getPublicOfflineRoutes(): string[] {
  return [...PUBLIC_OFFLINE_ROUTES];
}
